import ELKApi from 'elkjs/lib/elk-api.js';
import ELKBundled from 'elkjs/lib/elk.bundled.js';

declare const Worker: any;
declare const window: any;

export interface NetworkLayoutOptions {
  strategy: 'down-tree' | 'right-tree' | 'radial' | 'free-form';
  nodeSpacing?: number;
  rankSpacing?: number;
}

export interface NetworkLayoutResult {
  nodePositions: Map<string, { x: number; y: number }>;
}

function createElk(): { layout: (graph: any) => Promise<any> } {
  // Bundled ELK works in browsers (creates a blob-URL Web Worker internally).
  // This service is intentionally browser-only — the Compass view materializer
  // uses its own hierarchical grid layout because CF Workers can't host
  // `new Worker(blobUrl)`.
  const isBrowser = typeof window !== 'undefined';
  if (isBrowser) {
    return new (ELKBundled as any)();
  }
  // Node test runner or Bun — attempt the worker-thread path.
  try {
    const workerPath = require.resolve('elkjs/lib/elk-worker.min.js');
    return new (ELKApi as any)({
      workerFactory: () => new Worker(workerPath),
    });
  } catch {
    return new (ELKBundled as any)();
  }
}

interface SimpleNode {
  id: string;
  type?: string;
  width?: number;
  height?: number;
  parentId?: string;
}

interface SimpleEdge {
  id: string;
  from: string;
  to: string;
  type?: string;
}

export class NetworkLayoutService {
  private elk: { layout: (graph: any) => Promise<any> };

  constructor() {
    this.elk = createElk();
  }

  async applyLayout(
    nodes: SimpleNode[],
    edges: SimpleEdge[],
    options: NetworkLayoutOptions,
  ): Promise<NetworkLayoutResult> {
    if (nodes.length === 0) {
      return { nodePositions: new Map() };
    }

    const nodeSpacing = options.nodeSpacing ?? 60;
    const rankSpacing = options.rankSpacing ?? 80;

    // Importers tag containment edges as either 'contains' (ontology-level)
    // or 'containment' (canvas connector type). Treat both as parent→child
    // hierarchy so ELK can nest them via INCLUDE_CHILDREN.
    const isContainment = (t: string | undefined) => t === 'contains' || t === 'containment';
    const containmentEdgesAll = edges.filter((e) => isContainment(e.type));
    const nonContainmentEdges = edges.filter((e) => !isContainment(e.type));

    // ELK's hierarchy mode only supports a single parent per child — but
    // CFN/Terraform happily place a Lambda in multiple subnets, etc. The
    // first-seen parent wins; further containment edges for the same
    // child are dropped from the layout graph (they remain in the source
    // edge list for downstream callers that need full topology).
    const firstParentForChild = new Map<string, string>();
    const containmentEdges: typeof containmentEdgesAll = [];
    for (const e of containmentEdgesAll) {
      if (firstParentForChild.has(e.to)) continue;
      firstParentForChild.set(e.to, e.from);
      containmentEdges.push(e);
    }

    const childrenOf = new Map<string, string[]>();
    const hasParent = new Set<string>();
    for (const e of containmentEdges) {
      if (!childrenOf.has(e.from)) childrenOf.set(e.from, []);
      childrenOf.get(e.from)!.push(e.to);
      hasParent.add(e.to);
    }

    const direction = options.strategy === 'right-tree' ? 'RIGHT' : 'DOWN';
    // Use mrtree (multi-root tree) for the default down-tree strategy
    // because it produces compact aspect ratios on the shallow + wide
    // dependency DAGs that come out of CFN imports. layered's wrapping
    // options don't reliably compress 30-wide rows into a square. The
    // free-form/force fallback stays available for users who want it.
    let algorithm: string;
    if (options.strategy === 'free-form') {
      algorithm = 'org.eclipse.elk.force';
    } else if (options.strategy === 'right-tree') {
      algorithm = 'org.eclipse.elk.layered';
    } else {
      algorithm = 'org.eclipse.elk.mrtree';
    }

    function buildElkChildren(parentId: string | null): any[] {
      const childIds = parentId ? (childrenOf.get(parentId) || []) : nodes.filter((n) => !hasParent.has(n.id)).map((n) => n.id);
      const nodeMap = new Map(nodes.map((n) => [n.id, n]));

      return childIds
        .map((id) => nodeMap.get(id))
        .filter(Boolean)
        .map((n) => {
          const children = buildElkChildren(n!.id);
          const isContainer = children.length > 0;

          return {
            id: n!.id,
            width: isContainer ? undefined : (n!.width || 160),
            height: isContainer ? undefined : (n!.height || 60),
            ...(isContainer
              ? {
                  children,
                  layoutOptions: {
                    'elk.padding': '[top=40,left=20,bottom=20,right=20]',
                  },
                }
              : {}),
          };
        });
    }

    const elkGraph = {
      id: 'root',
      layoutOptions: {
        'elk.algorithm': algorithm,
        'elk.direction': direction,
        'elk.edgeRouting': 'ORTHOGONAL',
        'elk.aspectRatio': '0.6',
        'elk.spacing.nodeNode': String(nodeSpacing),
        'elk.layered.spacing.nodeNodeBetweenLayers': String(rankSpacing),
        'elk.spacing.edgeNode': '20',
        'elk.spacing.edgeEdge': '15',
        'elk.layered.spacing.edgeNodeBetweenLayers': '20',
        'elk.layered.spacing.edgeEdgeBetweenLayers': '10',
        'elk.layered.nodePlacement.strategy': 'NETWORK_SIMPLEX',
        'elk.layered.crossingMinimization.strategy': 'LAYER_SWEEP',
        'elk.layered.wrapping.strategy': 'MULTI_EDGE',
        'elk.layered.compaction.postCompaction.strategy': 'LEFT_RIGHT_CONSTRAINT_LOCKING',
        'elk.hierarchyHandling': 'INCLUDE_CHILDREN',
      },
      children: buildElkChildren(null),
      edges: nonContainmentEdges.map((e) => ({
        id: e.id,
        sources: [e.from],
        targets: [e.to],
      })),
    };

    const layouted = await this.elk.layout(elkGraph);
    return { nodePositions: this.extractPositions(layouted) };
  }

  private extractPositions(graph: any): Map<string, { x: number; y: number }> {
    const positions = new Map<string, { x: number; y: number }>();

    function walk(node: any, offsetX: number, offsetY: number) {
      if (node.id && node.id !== 'root') {
        positions.set(node.id, {
          x: (node.x ?? 0) + offsetX,
          y: (node.y ?? 0) + offsetY,
        });
      }
      if (node.children) {
        const cx = node.id === 'root' ? 0 : (node.x ?? 0) + offsetX;
        const cy = node.id === 'root' ? 0 : (node.y ?? 0) + offsetY;
        for (const child of node.children) {
          walk(child, cx, cy);
        }
      }
    }

    walk(graph, 0, 0);
    return positions;
  }
}
